import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Fingerprint, Clock, MapPin, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

function fmtWibDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const dt = d.toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Jakarta" });
    const tm = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Jakarta" });
    return `${dt}, ${tm}`;
  } catch { return "—"; }
}
function fmtWibTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Jakarta" });
  } catch { return "—"; }
}

const STATUS_BADGE = {
  IN_PROGRESS: { label: "Belum Pulang", cls: "bg-[#FFF7ED] text-[#9A3412] border-[#FED7AA]" },
  DONE: { label: "Sudah Pulang", cls: "bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]" },
  PERLU_VERIFIKASI: { label: "Perlu Verifikasi", cls: "bg-[#FEF2F2] text-[#991B1B] border-[#FECACA]" },
};

export default function Attendance() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmingLeave, setConfirmingLeave] = useState(false);
  const [gettingGps, setGettingGps] = useState(false);
  // Absen Pulang confirmation modal state — holds the fresh GPS reading so
  // the user can see the evidence being sent (Rule 5).
  const [leaveConfirm, setLeaveConfirm] = useState(null); // { lat, lng, accuracy, now, error? }
  // Feb 2026 — DINAS hari ini (bila ada). Backend `/attendance/duties/today`
  // return { duty: null | {...} } dan hanya me-list DINAS aktif untuk user
  // login pada tanggal WIB berjalan.
  const [duty, setDuty] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const { data: d } = await api.get("/attendance/me");
      setData(d);
      // Ambil DINAS hari ini sejajar dengan attendance/me; kegagalan tidak
      // fatal (endpoint baru, opsional untuk UX).
      try {
        const { data: dd } = await api.get("/attendance/duties/today");
        setDuty(dd?.duty || null);
      } catch { /* non-fatal */ }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Gagal memuat absensi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startLeave = () => {
    // Rule 5: fetch fresh GPS first, then show confirmation dialog. NEVER
    // trust a stale coordinate cached elsewhere.
    if (!navigator.geolocation) {
      toast.error("Perangkat tidak mendukung GPS");
      return;
    }
    setGettingGps(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGettingGps(false);
        setLeaveConfirm({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy || null,
          now: new Date(),
        });
      },
      (err) => {
        setGettingGps(false);
        toast.error(err?.message?.includes("denied") ? "Izin GPS ditolak. Aktifkan lokasi di pengaturan browser." : "Tidak dapat mengambil GPS. Coba di luar ruangan.");
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
    );
  };

  const submitLeave = async () => {
    if (!leaveConfirm) return;
    setConfirmingLeave(true);
    try {
      await api.post("/attendance/confirm-leave", {
        latitude: leaveConfirm.latitude,
        longitude: leaveConfirm.longitude,
        accuracy: leaveConfirm.accuracy,
      });
      toast.success("Absen pulang tersimpan");
      setLeaveConfirm(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Gagal absen pulang");
    } finally {
      setConfirmingLeave(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center gap-3 text-[#6B7280]">
        <Loader2 className="h-4 w-4 animate-spin" /> Memuat absensi…
      </div>
    );
  }

  if (!data?.absensi_enabled) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="rounded-lg border border-[#E5E7EB] bg-white p-8 text-center" data-testid="attendance-not-required">
          <Fingerprint className="h-10 w-10 mx-auto text-[#9CA3AF] mb-3" />
          <h2 className="font-display text-xl font-black tracking-tighter mb-1">Absensi Tidak Diwajibkan</h2>
          <p className="text-sm text-[#6B7280]">Akun Anda tidak memerlukan absensi lokasi.</p>
        </div>
      </div>
    );
  }

  const today = data.today;
  const status = today?.status || null;
  const badge = status ? STATUS_BADGE[status] : null;

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto space-y-6" data-testid="attendance-page">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Operasional</div>
        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter">Absensi Lokasi</h1>
        <p className="text-sm text-[#6B7280] mt-1">
          Server: <span className="font-mono-tab">{data.server_time_wib} WIB</span>
        </p>
      </div>

      {/* Feb 2026 — DINAS Hari Ini banner: informasi konteks lokasi kerja
          untuk teknisi supaya tahu bahwa validasi GPS check-in akan
          menggunakan target DINAS, bukan HQ/cabang. */}
      {duty && (
        <div
          className="rounded-lg border-2 border-[#3B82F6] bg-[#EFF6FF] p-4"
          data-testid="attendance-duty-banner"
        >
          <div className="flex items-start gap-3">
            <div className="shrink-0 h-9 w-9 rounded-md bg-[#0055FF] text-white flex items-center justify-center">
              <MapPin className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#1E40AF]">
                Dinas Hari Ini
              </div>
              <div className="font-display text-xl font-black text-[#0F172A] mt-0.5">
                {duty.tujuan}
              </div>
              {duty.keperluan && (
                <div className="text-sm text-[#1E3A8A] mt-0.5">{duty.keperluan}</div>
              )}
              {duty.start_date && duty.end_date && duty.start_date !== duty.end_date && (
                <div className="text-xs text-[#1E40AF] mt-1 font-mono">
                  Periode: {duty.start_date} → {duty.end_date}
                </div>
              )}
              <div className="text-[11px] text-[#4B5563] mt-1.5 font-mono">
                Target GPS: {Number(duty.latitude).toFixed(5)}, {Number(duty.longitude).toFixed(5)} · radius {duty.radius_meter}m
              </div>
              <div className="text-xs text-[#1E40AF] mt-1">
                Absen di lokasi ini — validasi GPS berpindah dari kantor ke titik DINAS.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Today card */}
      <div className="rounded-lg border border-[#E5E7EB] bg-white p-4 md:p-6 space-y-4" data-testid="attendance-today">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Hari Ini</div>
            <div className="font-display text-2xl font-black tracking-tighter">
              {new Date(data.server_date_wib + "T00:00:00+07:00").toLocaleDateString("id-ID", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}
            </div>
          </div>
          {badge && (
            <span className={`inline-flex items-center gap-1.5 h-7 px-3 rounded-full border text-xs font-bold ${badge.cls}`} data-testid="attendance-status-badge">
              {status === "DONE" ? <CheckCircle2 className="h-3.5 w-3.5" /> : status === "PERLU_VERIFIKASI" ? <AlertTriangle className="h-3.5 w-3.5" /> : <Clock className="h-3.5 w-3.5" />}
              {badge.label}
            </span>
          )}
        </div>

        {today ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-md border border-[#E5E7EB] p-3">
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF] mb-1">Lokasi</div>
              <div className="flex items-center gap-2 text-sm font-semibold">
                <MapPin className="h-4 w-4 text-[#0055FF]" />
                {today.attendance_location_name} <span className="text-[#9CA3AF] font-mono-tab">· {today.attendance_location_code}</span>
              </div>
            </div>
            <div className="rounded-md border border-[#E5E7EB] p-3">
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF] mb-1">Absen Masuk</div>
              <div className="text-sm font-semibold font-mono-tab" data-testid="attendance-checkin-at">{fmtWibTime(today.check_in_at)} WIB</div>
              <div className="text-[10px] text-[#9CA3AF] mt-0.5">Jarak: {Math.round(today.check_in_distance || 0)}m · {today.check_in_source}</div>
            </div>
            <div className="rounded-md border border-[#E5E7EB] p-3">
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF] mb-1">Absen Pulang</div>
              <div className="text-sm font-semibold font-mono-tab" data-testid="attendance-checkout-at">{today.check_out_at ? `${fmtWibTime(today.check_out_at)} WIB` : "—"}</div>
              {today.checkout_recorded_at && (
                <div className="text-[10px] text-[#9CA3AF] mt-0.5">Direkam: {fmtWibTime(today.checkout_recorded_at)} · {today.check_out_source}</div>
              )}
            </div>
            <div className="rounded-md border border-[#E5E7EB] p-3">
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF] mb-1">Terakhir di Lokasi</div>
              <div className="text-sm font-semibold font-mono-tab">{today.last_inside_at ? `${fmtWibTime(today.last_inside_at)} WIB` : "—"}</div>
              <div className="text-[10px] text-[#9CA3AF] mt-0.5">Fallback jika lupa Absen Pulang</div>
            </div>
          </div>
        ) : (
          <div className="rounded-md border border-dashed border-[#E5E7EB] p-6 text-center text-sm text-[#6B7280]" data-testid="attendance-empty-today">
            Belum absen hari ini. Sistem akan otomatis absen begitu Anda berada dalam radius lokasi.
          </div>
        )}

        {/* Absen Pulang button — enabled whenever an open record exists AND
            cutoff (18:00 WIB) has not passed. After cutoff, the record is
            auto-settled by backend and the button is hidden. */}
        {today && !today.check_out_at && (
          <div>
            {status === "PERLU_VERIFIKASI" ? (
              <div className="rounded-md border border-[#FECACA] bg-[#FEF2F2] p-3 text-sm text-[#991B1B]" data-testid="attendance-verify-msg">
                Absensi pulang belum dapat diverifikasi. Silakan hubungi Admin.
              </div>
            ) : data.after_cutoff ? (
              <div className="rounded-md border border-[#FED7AA] bg-[#FFF7ED] p-3 text-sm text-[#9A3412]" data-testid="attendance-cutoff-msg">
                Cutoff absensi (18:00 WIB) sudah lewat. Sistem akan menutup record ini menggunakan bukti terakhir (last_inside_at).
              </div>
            ) : (
              <>
                <button
                  type="button"
                  onClick={startLeave}
                  disabled={confirmingLeave || gettingGps}
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-[#065F46] hover:bg-[#064E3B] text-white text-sm font-bold uppercase tracking-widest disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="attendance-absen-pulang"
                >
                  {gettingGps ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  Absen Pulang
                </button>
                <p className="mt-2 text-[11px] text-[#9CA3AF]">Sistem akan mengecek lokasi Anda saat tombol ditekan.</p>
              </>
            )}
          </div>
        )}
      </div>

      {/* Allowed locations */}
      <div className="rounded-lg border border-[#E5E7EB] bg-white p-4 md:p-6">
        <div className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF] mb-3">Lokasi Absensi Anda</div>
        {data.allowed_locations?.length ? (
          <ul className="space-y-2">
            {data.allowed_locations.map((l) => (
              <li key={l.id} className="flex items-center justify-between gap-2 py-1.5 border-b border-[#F3F4F6] last:border-b-0 text-sm">
                <span className="font-semibold">{l.name} <span className="text-[#9CA3AF] font-mono-tab">· {l.code}</span></span>
                <span className="text-[10px] text-[#9CA3AF] font-mono-tab">R {l.radius_meter}m · Acc ≤{l.accuracy_threshold_meter}m</span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-sm text-[#9CA3AF]">Belum ada lokasi absensi yang diizinkan. Hubungi Admin.</div>
        )}
      </div>

      {/* History */}
      <div className="rounded-lg border border-[#E5E7EB] bg-white overflow-hidden">
        <div className="px-4 md:px-6 py-3 border-b border-[#E5E7EB]">
          <div className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Riwayat 30 Hari</div>
        </div>
        {data.history?.length ? (
          <ul className="divide-y divide-[#F3F4F6]" data-testid="attendance-history">
            {data.history.map((h) => {
              const b = STATUS_BADGE[h.status] || null;
              return (
                <li key={h.id} className="px-4 md:px-6 py-3 flex items-center justify-between gap-2 flex-wrap">
                  <div>
                    <div className="text-sm font-bold" data-testid={`attendance-row-${h.attendance_date}`}>{h.attendance_date}</div>
                    <div className="text-[11px] text-[#6B7280]">{h.attendance_location_name}</div>
                  </div>
                  <div className="text-xs font-mono-tab text-[#4B5563]">
                    {fmtWibTime(h.check_in_at)} → {h.check_out_at ? fmtWibTime(h.check_out_at) : "—"}
                  </div>
                  {b && <span className={`inline-flex items-center h-6 px-2 rounded-full border text-[10px] font-bold ${b.cls}`}>{b.label}</span>}
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="px-6 py-8 text-center text-sm text-[#9CA3AF]">Belum ada riwayat absensi.</div>
        )}
      </div>

      {/* Absen Pulang confirmation modal (Rule 5) — evidence-based preview */}
      {leaveConfirm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !confirmingLeave && setLeaveConfirm(null)}>
          <div className="bg-white rounded-lg max-w-md w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="absen-pulang-confirm">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Konfirmasi</div>
              <h2 className="font-display text-2xl font-black tracking-tighter">Absen Pulang?</h2>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-[#6B7280]">Tanggal</span><span className="font-semibold">{leaveConfirm.now.toLocaleDateString("id-ID", { weekday: "long", day: "2-digit", month: "long", year: "numeric", timeZone: "Asia/Jakarta" })}</span></div>
              <div className="flex justify-between"><span className="text-[#6B7280]">Waktu Server</span><span className="font-mono-tab font-semibold" data-testid="confirm-server-time">{data?.server_time_wib} WIB</span></div>
              <div className="flex justify-between"><span className="text-[#6B7280]">Lokasi</span><span className="font-semibold">{today?.attendance_location_name}</span></div>
              <div className="flex justify-between"><span className="text-[#6B7280]">Koordinat</span><span className="font-mono-tab text-xs">{leaveConfirm.latitude.toFixed(5)}, {leaveConfirm.longitude.toFixed(5)}</span></div>
              <div className="flex justify-between">
                <span className="text-[#6B7280]">Akurasi GPS</span>
                <span className={`font-mono-tab font-semibold ${leaveConfirm.accuracy && leaveConfirm.accuracy > 50 ? "text-[#991B1B]" : "text-[#065F46]"}`}>
                  {leaveConfirm.accuracy ? `${Math.round(leaveConfirm.accuracy)}m` : "—"}
                </span>
              </div>
            </div>
            <p className="text-[11px] text-[#6B7280] leading-relaxed">
              Sistem akan mencatat jam pulang sesuai waktu server saat Anda menekan Konfirmasi. Waktu tidak dapat diedit sendiri.
            </p>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setLeaveConfirm(null)}
                disabled={confirmingLeave}
                className="h-10 px-4 rounded-md border border-[#E5E7EB] text-sm font-semibold hover:bg-[#F8F9FA]"
                data-testid="absen-pulang-cancel"
              >
                Batal
              </button>
              <button
                type="button"
                onClick={submitLeave}
                disabled={confirmingLeave}
                className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-[#065F46] hover:bg-[#064E3B] text-white text-sm font-bold uppercase tracking-widest disabled:opacity-40"
                data-testid="absen-pulang-submit"
              >
                {confirmingLeave ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                Konfirmasi Pulang
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
